import React, { useState } from "react";
import { FileText, Plus, Pencil, ArrowRight } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import MutationErrorAlert from "../components/MutationErrorAlert";
import StatusBadge from "../components/StatusBadge";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import { getFooterConfigurations, createFooterConfiguration, updateFooterConfiguration, describeMutationError } from "../../../lib/emailMarketingApi";
import { BROADCAST_ENVIRONMENT_VALUES } from "../broadcasts/broadcastStatusTransitions";

/**
 * FooterConfigurationsPanel — EMAILS-IMPL-033
 *
 * Governed Email Footer Configuration authoring (BRD-SPEC-001 §10.3):
 * browse the tenant-wide, Environment-scoped catalog, create a Draft, edit
 * a Draft's fields freely, Approve (freezing every field permanently), or
 * Retire an Approved record (terminal). Mirrors SenderConfigurationsPanel's
 * identical lifecycle contract exactly -- no edit affordance is ever
 * offered once a record leaves Draft, and the backend enforces this
 * independently regardless.
 */

function emptyCreateForm() {
  return {
    name: "", environment: "", brandName: "", legalEntityName: "", copyrightText: "",
    mailingAddress: "", privacyPolicyUrl: "", termsOfUseUrl: "", complianceInstructionalText: "",
  };
}

export default function FooterConfigurationsPanel() {
  const listFn = React.useCallback(() => getFooterConfigurations(), []);
  const createFn = React.useCallback(
    (payload) => createFooterConfiguration(payload),
    []
  );
  const updateFn = React.useCallback((id, payload) => updateFooterConfiguration(id, payload), []);
  const getIdFn = React.useCallback((row) => row.id, []);

  const { items, loading, error, mutating, mutationError, clearMutationError, createItem, updateItem } = useEmailCatalogEntityList({
    list: listFn, create: createFn, update: updateFn, getId: getIdFn,
  });

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm());
  const [editingFooter, setEditingFooter] = useState(null);
  const [editForm, setEditForm] = useState(emptyCreateForm());

  // Lifecycle-transition actions (Approve/Retire) get their own error
  // surface, distinct from the Create/Edit dialogs' own mutationError --
  // they are not dialog-form submissions.
  const [transitionError, setTransitionError] = useState(null);

  const openCreate = () => {
    setCreateForm(emptyCreateForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      await createItem({
        name: createForm.name.trim(),
        environment: createForm.environment,
        brandName: createForm.brandName.trim(),
        legalEntityName: createForm.legalEntityName.trim() || undefined,
        copyrightText: createForm.copyrightText.trim(),
        mailingAddress: createForm.mailingAddress.trim(),
        privacyPolicyUrl: createForm.privacyPolicyUrl.trim(),
        termsOfUseUrl: createForm.termsOfUseUrl.trim() || undefined,
        complianceInstructionalText: createForm.complianceInstructionalText.trim() || undefined,
      });
      setCreateOpen(false);
    } catch {
      // handled via mutationError
    }
  };

  const openEdit = (footer) => {
    setEditingFooter(footer);
    setEditForm({
      name: footer.name, environment: footer.environment, brandName: footer.brand_name,
      legalEntityName: footer.legal_entity_name || "", copyrightText: footer.copyright_text,
      mailingAddress: footer.mailing_address, privacyPolicyUrl: footer.privacy_policy_url,
      termsOfUseUrl: footer.terms_of_use_url || "", complianceInstructionalText: footer.compliance_instructional_text || "",
    });
    clearMutationError();
  };

  const submitEdit = async () => {
    try {
      await updateItem(editingFooter.id, {
        name: editForm.name.trim() !== editingFooter.name ? editForm.name.trim() : undefined,
        environment: editForm.environment !== editingFooter.environment ? editForm.environment : undefined,
        brandName: editForm.brandName.trim() !== editingFooter.brand_name ? editForm.brandName.trim() : undefined,
        legalEntityName:
          editForm.legalEntityName.trim() !== (editingFooter.legal_entity_name || "") ? editForm.legalEntityName.trim() || null : undefined,
        copyrightText: editForm.copyrightText.trim() !== editingFooter.copyright_text ? editForm.copyrightText.trim() : undefined,
        mailingAddress: editForm.mailingAddress.trim() !== editingFooter.mailing_address ? editForm.mailingAddress.trim() : undefined,
        privacyPolicyUrl: editForm.privacyPolicyUrl.trim() !== editingFooter.privacy_policy_url ? editForm.privacyPolicyUrl.trim() : undefined,
        termsOfUseUrl:
          editForm.termsOfUseUrl.trim() !== (editingFooter.terms_of_use_url || "") ? editForm.termsOfUseUrl.trim() || null : undefined,
        complianceInstructionalText:
          editForm.complianceInstructionalText.trim() !== (editingFooter.compliance_instructional_text || "")
            ? editForm.complianceInstructionalText.trim() || null
            : undefined,
      });
      setEditingFooter(null);
    } catch {
      // handled via mutationError
    }
  };

  const transition = async (footerConfigurationId, status) => {
    setTransitionError(null);
    try {
      await updateItem(footerConfigurationId, { status });
    } catch (err) {
      setTransitionError(describeMutationError(err, "Failed to update status."));
    }
  };

  if (loading) return <CatalogLoadingCard testId="footers-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="footers-error" />;

  return (
    <div className="space-y-4" data-testid="footers-panel">
      <CatalogTable
        testId="footers-table"
        title="Email Footer Configurations"
        icon={FileText}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            data-testid="new-footer-button"
          >
            <Plus className="h-4 w-4" /> New Footer Configuration
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.id}
        onRowClick={(row) => setExpandedId(expandedId === row.id ? null : row.id)}
        emptyTitle="No Email Footer Configurations yet"
        emptySubtitle='Create one with "New Footer Configuration" to get started.'
        columns={[
          {
            key: "name",
            label: "Name",
            render: (row) => <span className="text-sm font-medium text-slate-100">{row.name}</span>,
          },
          {
            key: "environment",
            label: "Environment",
            render: (row) => (
              <span
                className={`text-xs font-semibold uppercase tracking-wide ${row.environment === "Production" ? "text-rose-300" : "text-sky-300"}`}
                data-testid={`footer-environment-${row.id}`}
              >
                {row.environment}
              </span>
            ),
          },
          {
            key: "brand",
            label: "Brand",
            render: (row) => (
              <div>
                <div className="text-sm text-slate-200">{row.brand_name}</div>
                <div className="text-xs text-slate-400">{row.mailing_address}</div>
              </div>
            ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`footer-status-${row.id}`} /> },
        ]}
      />

      {items
        .filter((row) => row.id === expandedId)
        .map((row) => (
          <div key={row.id} className="space-y-3 rounded-lg border border-white/10 bg-white/5 p-4" data-testid={`footer-detail-${row.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Detail label="Name" value={row.name} />
                <Detail label="Environment" value={row.environment} />
                <Detail label="Status" value={<StatusBadge status={row.status} testId={`footer-detail-status-${row.id}`} />} />
                <Detail label="Brand Name" value={row.brand_name} />
                {row.legal_entity_name && <Detail label="Legal Entity" value={row.legal_entity_name} />}
                <Detail label="Copyright" value={row.copyright_text} />
                <Detail label="Mailing Address" value={row.mailing_address} />
                <Detail label="Privacy Policy URL" value={row.privacy_policy_url} mono />
                {row.terms_of_use_url && <Detail label="Terms of Use URL" value={row.terms_of_use_url} mono />}
                {row.compliance_instructional_text && (
                  <Detail label="Compliance Instructional Text" value={row.compliance_instructional_text} />
                )}
              </div>
              {row.status === "Draft" && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-white/10 text-slate-200"
                  onClick={() => openEdit(row)}
                  data-testid={`edit-footer-${row.id}`}
                >
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </Button>
              )}
            </div>

            <div className="flex flex-wrap gap-2 border-t border-white/10 pt-3">
              {row.status === "Draft" && (
                <Button
                  type="button"
                  size="sm"
                  className="gap-1.5 bg-emerald-600 text-white hover:bg-emerald-500"
                  disabled={mutating}
                  onClick={() => transition(row.id, "Approved")}
                  data-testid={`approve-footer-${row.id}`}
                >
                  <ArrowRight className="h-3.5 w-3.5" /> Approve
                </Button>
              )}
              {row.status === "Approved" && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="gap-1.5 border-amber-400/30 text-amber-300"
                  disabled={mutating}
                  onClick={() => transition(row.id, "Retired")}
                  data-testid={`retire-footer-${row.id}`}
                >
                  <ArrowRight className="h-3.5 w-3.5" /> Retire
                </Button>
              )}
              {row.status === "Retired" && (
                <p className="text-xs text-slate-500" data-testid={`footer-retired-note-${row.id}`}>
                  Retired — no further changes possible. Create a new Footer Configuration to change footer content.
                </p>
              )}
            </div>
            {transitionError && <MutationErrorAlert error={transitionError} testId={`footer-transition-error-${row.id}`} />}
          </div>
        ))}

      <EntityFormDialog
        testId="create-footer-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Email Footer Configuration"
        description="Governed footer/company identity, scoped to one Broadcast Environment. Operators select an Approved configuration on a Broadcast — never type footer values directly."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Footer Configuration"
      >
        <FooterFields form={createForm} setForm={setCreateForm} testPrefix="footer" />
      </EntityFormDialog>

      {editingFooter && (
        <EntityFormDialog
          testId="edit-footer-dialog"
          open={!!editingFooter}
          onOpenChange={(open) => !open && setEditingFooter(null)}
          title={`Edit ${editingFooter.name}`}
          description="Draft configurations may be edited freely. Approving freezes every field permanently."
          onSubmit={submitEdit}
          submitting={mutating}
          error={mutationError}
          submitLabel="Save Changes"
        >
          <FooterFields form={editForm} setForm={setEditForm} testPrefix="footer-edit" />
        </EntityFormDialog>
      )}
    </div>
  );
}

function FooterFields({ form, setForm, testPrefix }) {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-name`}>Name</Label>
        <Input
          id={`${testPrefix}-name`}
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. iLifeQuotes Standard Footer"
          required
          data-testid={`${testPrefix}-name-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-environment-select`}>Environment</Label>
        <Select value={form.environment} onValueChange={(v) => setForm((f) => ({ ...f, environment: v }))}>
          <SelectTrigger id={`${testPrefix}-environment-select`} data-testid={`${testPrefix}-environment-select`}>
            <SelectValue placeholder="Select an Environment" />
          </SelectTrigger>
          <SelectContent>
            {BROADCAST_ENVIRONMENT_VALUES.map((env) => (
              <SelectItem key={env} value={env} data-testid={`${testPrefix}-environment-option-${env}`}>
                {env}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-brand-name`}>Brand Name</Label>
        <Input
          id={`${testPrefix}-brand-name`}
          value={form.brandName}
          onChange={(e) => setForm((f) => ({ ...f, brandName: e.target.value }))}
          placeholder="e.g. iLifeQuotes"
          required
          data-testid={`${testPrefix}-brand-name-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-legal-entity-name`}>Legal Entity Name (optional)</Label>
        <Input
          id={`${testPrefix}-legal-entity-name`}
          value={form.legalEntityName}
          onChange={(e) => setForm((f) => ({ ...f, legalEntityName: e.target.value }))}
          placeholder="e.g. Safe Money Guys LLC"
          data-testid={`${testPrefix}-legal-entity-name-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-copyright-text`}>Copyright Text</Label>
        <Input
          id={`${testPrefix}-copyright-text`}
          value={form.copyrightText}
          onChange={(e) => setForm((f) => ({ ...f, copyrightText: e.target.value }))}
          placeholder="e.g. Copyright © 2026 iLifeQuotes. All rights reserved."
          required
          data-testid={`${testPrefix}-copyright-text-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-mailing-address`}>Mailing Address</Label>
        <Textarea
          id={`${testPrefix}-mailing-address`}
          value={form.mailingAddress}
          onChange={(e) => setForm((f) => ({ ...f, mailingAddress: e.target.value }))}
          placeholder="e.g. 5379 Lyons Rd #3125, Coconut Creek, FL 33073"
          required
          data-testid={`${testPrefix}-mailing-address-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-privacy-policy-url`}>Privacy Policy URL</Label>
        <Input
          id={`${testPrefix}-privacy-policy-url`}
          type="url"
          value={form.privacyPolicyUrl}
          onChange={(e) => setForm((f) => ({ ...f, privacyPolicyUrl: e.target.value }))}
          placeholder="e.g. https://ilifequotes.com/privacy"
          required
          data-testid={`${testPrefix}-privacy-policy-url-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-terms-of-use-url`}>Terms of Use URL (optional)</Label>
        <Input
          id={`${testPrefix}-terms-of-use-url`}
          type="url"
          value={form.termsOfUseUrl}
          onChange={(e) => setForm((f) => ({ ...f, termsOfUseUrl: e.target.value }))}
          placeholder="e.g. https://ilifequotes.com/terms"
          data-testid={`${testPrefix}-terms-of-use-url-input`}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testPrefix}-compliance-instructional-text`}>Compliance Instructional Text (optional)</Label>
        <Textarea
          id={`${testPrefix}-compliance-instructional-text`}
          value={form.complianceInstructionalText}
          onChange={(e) => setForm((f) => ({ ...f, complianceInstructionalText: e.target.value }))}
          placeholder="e.g. Want to change how you receive these emails? You can unsubscribe from this list or manage your preferences."
          data-testid={`${testPrefix}-compliance-instructional-text-input`}
        />
      </div>
    </>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
